import {
  createFileRoute,
  Link,
  useNavigate,
} from "@tanstack/react-router";
import { useState, useEffect } from "react";
import {
  Envelope,
  Phone,
  Calendar,
  Shield,
  Bell,
  Globe,
  CaretRight,
  Crown,
  Clock,
  CheckCircle,
  Gift,
  Confetti,
  XCircle,
  Download,
  Info,
  SignOut,
} from "@phosphor-icons/react";
import { useQuery, useAction, useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import { changeLanguage } from "@shared/lib/i18n";
import { useTranslation } from "react-i18next";
import { getStoredUserSession } from "@shared/hooks/useUserSession";

export const Route = createFileRoute("/app/profile")({
  component: ProfilePage,
  validateSearch: (search: Record<string, unknown>) => ({
    success: search.success === "true",
    canceled: search.canceled === "true",
    session_id: (search.session_id as string) || undefined,
  }),
});

function ProfilePage() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const currentLang = (i18n.language || "ro") as "ro" | "en";
  const { userId, sessionToken } = getStoredUserSession();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showCanceledModal, setShowCanceledModal] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [showGdprInfo, setShowGdprInfo] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [requestHardDelete, setRequestHardDelete] = useState(false);

  const cancelSubscription = useAction(api.stripe.cancelUserSubscription);
  const archiveAccount = useMutation(api.users.archiveUserAccount);
  const exportData = useQuery(
    api.users.exportUserData,
    userId && sessionToken ? { userId, sessionToken } : "skip"
  );


  // Handle Stripe redirect - check both search params and URL directly
  useEffect(() => {
    // Clean URL immediately to remove any sensitive params
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get("success") === "true";
    const canceled = urlParams.get("canceled") === "true";

    // Clean URL immediately if there are any params (remove session_id, payment_intent, etc.)
    if (urlParams.toString()) {
      window.history.replaceState({}, "", "/app/profile");
    }

    if (success) {
      setShowSuccessModal(true);
      // Start polling for status update
      setIsCheckingStatus(true);
    } else if (canceled) {
      setShowCanceledModal(true);
    }
  }, []);

  const user = useQuery(
    api.auth.getCurrentUser,
    userId ? { userId } : "skip"
  );

  // Force refresh user data when membership status changes
  useEffect(() => {
    if (user && user.membershipStatus === "active" && isCheckingStatus) {
      // Membership became active, reload to show updated status
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  }, [user?.membershipStatus, isCheckingStatus]);

  // Poll for membership status update after successful payment
  useEffect(() => {
    if (isCheckingStatus && user && userId) {
      // Check if membership is now active
      if (user.membershipStatus === "active") {
        setIsCheckingStatus(false);
        // Force a small delay to ensure UI updates
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        // Keep checking every 2 seconds for up to 30 seconds
        const timeout = setTimeout(() => {
          setIsCheckingStatus(false);
        }, 30000);

        const interval = setInterval(() => {
          // Query will auto-refresh, but we can force it
          if (user && user.membershipStatus === "active") {
            clearInterval(interval);
            clearTimeout(timeout);
            setIsCheckingStatus(false);
            setTimeout(() => {
              window.location.reload();
            }, 500);
          }
        }, 2000);

        return () => {
          clearInterval(interval);
          clearTimeout(timeout);
        };
      }
    }
  }, [isCheckingStatus, user?.membershipStatus, userId]);

  // Funcție logout
  const handleLogout = async () => {
    const { trackEvent, resetUser } = await import("@shared/lib/posthog-lazy");
    await trackEvent("user_logged_out");
    await resetUser();
    localStorage.removeItem("userId");
    sessionStorage.clear();
    window.location.href = "/";
  };

  // Formatare dată
  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString(
      currentLang === "ro" ? "ro-RO" : "en-US",
      {
        day: "numeric",
        month: "long",
        year: "numeric",
      }
    );
  };

  if (user === undefined) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-10 h-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (user === null) {
    return null;
  }

  const daysRemaining = user.membershipExpiryDate
    ? Math.ceil(
        (user.membershipExpiryDate - Date.now()) / (1000 * 60 * 60 * 24)
      )
    : 0;

  const membershipStatusColors = {
    active: "bg-success/10 text-success",
    trial: "bg-accent/20 text-accent-dark",
    expired: "bg-error/10 text-error",
  };

  return (
    <>
      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center animate-in fade-in zoom-in duration-300">
            {isCheckingStatus ? (
              <>
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="w-8 h-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                </div>
                <h3 className="text-xl font-bold text-text mb-2">
                  {currentLang === "ro" ? "Se procesează..." : "Processing..."}
                </h3>
                <p className="text-text-muted mb-4">
                  {currentLang === "ro"
                    ? "Verificăm statusul abonamentului tău..."
                    : "Checking your membership status..."}
                </p>
              </>
            ) : (
              <>
                <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Confetti size={32} className="text-success" />
                </div>
                <h3 className="text-xl font-bold text-text mb-2">
                  {currentLang === "ro" ? "Felicitări!" : "Congratulations!"}
                </h3>
                <p className="text-text-muted mb-4">
                  {currentLang === "ro"
                    ? "Abonamentul tău a fost activat cu succes. Bucură-te de reduceri nelimitate!"
                    : "Your membership has been activated successfully. Enjoy unlimited discounts!"}
                </p>
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    window.location.reload();
                  }}
                  className="w-full py-3 bg-primary text-white font-semibold rounded-xl"
                >
                  {currentLang === "ro" ? "Super!" : "Awesome!"}
                </button>
              </>
            )}
          </div>
        </div>
      )}
      {/* Delete Account Confirm Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <div className="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle size={24} className="text-error" />
            </div>
            <h3 className="text-xl font-bold text-text mb-2 text-center">
              {currentLang === "ro" ? "Ștergi contul?" : "Delete account?"}
            </h3>
            <div className="space-y-3 mb-6">
              <p className="text-text-muted text-sm text-center">
                {currentLang === "ro"
                  ? "Contul tău va fi arhivat. Conform GDPR:"
                  : "Your account will be archived. According to GDPR:"}
              </p>
              <ul className="text-sm text-text-muted space-y-2 list-disc list-inside">
                <li>
                  {currentLang === "ro"
                    ? "Datele tale vor fi păstrate 60 zile pentru prevenirea fraudului"
                    : "Your data will be kept for 60 days for fraud prevention"}
                </li>
                <li>
                  {currentLang === "ro"
                    ? "După 60 zile, datele sensibile (email, telefon) vor fi anonimizate automat"
                    : "After 60 days, sensitive data (email, phone) will be automatically anonymized"}
                </li>
                <li>
                  {currentLang === "ro"
                    ? "Datele de facturare (reduceri, abonamente) se păstrează pentru obligații legale"
                    : "Billing data (redemptions, subscriptions) are kept for legal obligations"}
                </li>
                <li className="font-semibold text-error">
                  {currentLang === "ro"
                    ? "Email-ul și telefonul vor fi blocate pentru 60 zile"
                    : "Email and phone will be blocked for 60 days"}
                </li>
              </ul>
              <button
                onClick={() => setShowGdprInfo(true)}
                className="w-full mt-3 py-2 text-sm text-primary hover:underline flex items-center justify-center gap-2"
              >
                <Info size={16} />
                {currentLang === "ro" ? "Află mai multe despre GDPR" : "Learn more about GDPR"}
              </button>
              <label className="flex items-start gap-3 mt-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={requestHardDelete}
                  onChange={(e) => setRequestHardDelete(e.target.checked)}
                  size={16} className="mt-1 text-primary border-gray-300 rounded focus:ring-primary"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-text">
                    {currentLang === "ro"
                      ? "Solicită ștergere completă după anonimizare"
                      : "Request complete deletion after anonymization"}
                  </p>
                  <p className="text-xs text-text-muted mt-1">
                    {currentLang === "ro"
                      ? "După anonimizare (60 zile), contul va fi șters complet. Datele de facturare vor rămâne pentru obligații legale."
                      : "After anonymization (60 days), account will be completely deleted. Billing data will remain for legal obligations."}
                  </p>
                </div>
              </label>
            </div>
            {deleteError && (
              <p className="text-error text-sm mb-4 text-center">{deleteError}</p>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteError(null);
                  setRequestHardDelete(false);
                }}
                disabled={isDeleting}
                className="flex-1 py-3 bg-gray-100 text-text font-semibold rounded-xl"
              >
                {currentLang === "ro" ? "Anulează" : "Cancel"}
              </button>
              <button
                onClick={async () => {
                  if (!userId) return;
                  setIsDeleting(true);
                  setDeleteError(null);
                  try {
                    const result = await archiveAccount({ 
                      userId,
                      sessionToken: sessionToken!,
                      requestHardDelete: requestHardDelete || undefined
                    });
                    if (result.success) {
                      // Logout and redirect
                      localStorage.removeItem("userId");
                      sessionStorage.clear();
                      navigate({ to: "/" });
                    } else {
                      setDeleteError(result.error || "Failed to delete account");
                    }
                  } catch (err) {
                    setDeleteError(
                      currentLang === "ro"
                        ? "A apărut o eroare"
                        : "An error occurred"
                    );
                  } finally {
                    setIsDeleting(false);
                  }
                }}
                disabled={isDeleting}
                className="flex-1 py-3 bg-error text-white font-semibold rounded-xl disabled:opacity-50"
              >
                {isDeleting
                  ? currentLang === "ro" ? "Se șterge..." : "Deleting..."
                  : currentLang === "ro" ? "Șterge contul" : "Delete account"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Cancel Subscription Confirm Modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center">
            <div className="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle size={24} className="text-error" />
            </div>
            <h3 className="text-xl font-bold text-text mb-2">
              {currentLang === "ro" ? "Anulezi abonamentul?" : "Cancel subscription?"}
            </h3>
            <p className="text-text-muted mb-4">
              {currentLang === "ro"
                ? `Abonamentul va rămâne activ până la ${user.membershipExpiryDate ? formatDate(user.membershipExpiryDate) : "expirare"}. După aceea, nu vei mai avea acces la reduceri.`
                : `Your subscription will remain active until ${user.membershipExpiryDate ? formatDate(user.membershipExpiryDate) : "expiration"}. After that, you won't have access to discounts.`}
            </p>
            {cancelError && (
              <p className="text-error text-sm mb-4">{cancelError}</p>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCancelConfirm(false);
                  setCancelError(null);
                }}
                disabled={isCancelling}
                className="flex-1 py-3 bg-gray-100 text-text font-semibold rounded-xl"
              >
                {currentLang === "ro" ? "Înapoi" : "Back"}
              </button>
              <button
                onClick={async () => {
                  if (!userId) return;
                  setIsCancelling(true);
                  setCancelError(null);
                  try {
                    const result = await cancelSubscription({ userId, sessionToken: sessionToken! });
                    if (result.success) {
                      setShowCancelConfirm(false);
                      window.location.reload();
                    } else {
                      setCancelError(result.error || "Failed to cancel");
                    }
                  } catch (err) {
                    setCancelError("An error occurred");
                  } finally {
                    setIsCancelling(false);
                  }
                }}
                disabled={isCancelling}
                className="flex-1 py-3 bg-error text-white font-semibold rounded-xl disabled:opacity-50"
              >
                {isCancelling
                  ? currentLang === "ro" ? "Se anulează..." : "Cancelling..."
                  : currentLang === "ro" ? "Anulează" : "Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Canceled Modal */}
      {showCanceledModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center animate-in fade-in zoom-in duration-300">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle size={24} className="text-text-muted" />
            </div>
            <h3 className="text-xl font-bold text-text mb-2">
              {currentLang === "ro" ? "Plată anulată" : "Payment canceled"}
            </h3>
            <p className="text-text-muted mb-4">
              {currentLang === "ro"
                ? "Nu s-a efectuat nicio plată. Poți încerca din nou oricând."
                : "No payment was made. You can try again anytime."}
            </p>
            <button
              onClick={() => setShowCanceledModal(false)}
              className="w-full py-3 bg-gray-100 text-text font-semibold rounded-xl"
            >
              {currentLang === "ro" ? "Închide" : "Close"}
            </button>
          </div>
        </div>
      )}
      <div className="px-4 py-6 pt-[100px] space-y-6">
        {/* Membership card */}
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                <Crown size={20} className="text-primary" />
              </div>
              <div>
                <p className="font-semibold text-text">
                  {currentLang === "ro" ? "Abonament" : "Membership"}
                </p>
                <p className="text-sm text-text-muted capitalize">
                  {user.membershipType}
                </p>
              </div>
            </div>
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                membershipStatusColors[user.membershipStatus as keyof typeof membershipStatusColors]
              }`}
            >
              {user.membershipStatus === "active"
                ? currentLang === "ro"
                  ? "Activ"
                  : "Active"
                : user.membershipStatus === "trial"
                  ? "Trial"
                  : currentLang === "ro"
                    ? "Expirat"
                    : "Expired"}
            </span>
          </div>

          <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl">
            <Clock size={16} className="text-text-muted" />
            <div className="flex-1">
              <span className="text-sm text-text">
                {daysRemaining > 0 ? (
                  <>
                    {currentLang === "ro"
                      ? `Expiră în ${daysRemaining} zile`
                      : `Expires in ${daysRemaining} days`}
                    {user.membershipExpiryDate && (
                      <span className="text-text-muted ml-1">
                        ({formatDate(user.membershipExpiryDate)})
                      </span>
                    )}
                  </>
                ) : (
                  currentLang === "ro" ? "Expirat" : "Expired"
                )}
              </span>
              {user.subscriptionCancelledAt && user.membershipStatus === "active" && (
                <p className="text-xs text-error mt-1">
                  {currentLang === "ro"
                    ? "⚠️ Programat pentru anulare la expirare"
                    : "⚠️ Scheduled for cancellation at period end"}
                </p>
              )}
            </div>
          </div>

          {(user.membershipStatus === "trial" ||
            user.membershipStatus === "expired" ||
            (daysRemaining <= 7 && daysRemaining > 0)) && (
            <Link
              to="/app/membership"
              className="w-full mt-4 py-3 bg-primary text-white font-semibold rounded-xl block text-center"
            >
              {user.membershipStatus === "trial"
                ? currentLang === "ro"
                  ? "Upgrade la Premium"
                  : "Upgrade to Premium"
                : user.membershipStatus === "expired"
                  ? currentLang === "ro"
                    ? "Reactivează abonamentul"
                    : "Reactivate membership"
                  : currentLang === "ro"
                    ? "Reînnoiește acum"
                    : "Renew now"}
            </Link>
          )}
        </div>

        {/* suuper pay: recompense și chitanțe */}
        <div className="grid grid-cols-2 gap-3">
          <Link to="/app/rewards" className="bg-white rounded-2xl p-4 shadow-sm block" data-testid="profile-rewards-link">
            <p className="font-semibold text-text">Recompensele mele</p>
            <p className="text-xs text-text-muted mt-1">
              {currentLang === "ro" ? "Vizitele plătite prin RoPay" : "Visits paid through RoPay"}
            </p>
          </Link>
          <Link to="/app/receipts" className="bg-white rounded-2xl p-4 shadow-sm block" data-testid="profile-receipts-link">
            <p className="font-semibold text-text">Chitanțe</p>
            <p className="text-xs text-text-muted mt-1">
              {currentLang === "ro" ? "Plățile confirmate" : "Confirmed payments"}
            </p>
          </Link>
        </div>

        {/* Banner invitații */}
        {(user.activatedInvitesCount ?? 0) < 5 && (
          <Link
            to="/app/invites"
            className="block bg-gradient-to-r from-primary to-accent-dark rounded-2xl p-5 text-white relative overflow-hidden"
          >
            <div className="w-32 h-32 absolute top-0 right-0 bg-white/10 rounded-full -mr-16 -mt-16" />
            <div className="w-24 h-24 absolute bottom-0 left-0 bg-white/10 rounded-full -ml-12 -mb-12" />
            <div className="relative z-10">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <p className="text-2xl font-bold mb-1 text-white">
                    {currentLang === "ro"
                      ? "Invită 5 prieteni și primești 7 zile GRATIS!"
                      : "Invite 5 friends and get 7 days FREE!"}
                  </p>
                  <p className="text-white/90 text-xs">
                    {currentLang === "ro"
                      ? "Fiecare prieten care invitat care iși face cont cu acelasi număr de telefon = 1 zi bonus"
                      : "Each friend who verifies phone = 1 bonus day"}
                  </p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Gift size={20} />
                </div>
              </div>

              {/* Progress bar compact */}
              <div className="mb-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium">
                    {currentLang === "ro" ? "Progres" : "Progress"}
                  </span>
                  <span className="text-base font-bold">
                    {(user.activatedInvitesCount ?? 0)}/5
                  </span>
                </div>
                <div className="w-full bg-white/20 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-white h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.min(((user.activatedInvitesCount ?? 0) / 5) * 100, 100)}%`,
                    }}
                  />
                </div>
              </div>

              <p className="text-xs text-white/80">
                {currentLang === "ro"
                  ? `${5 - (user.activatedInvitesCount ?? 0)} invitații rămase`
                  : `${5 - (user.activatedInvitesCount ?? 0)} invites remaining`}
              </p>
            </div>
          </Link>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-2xl font-bold text-text">
              {user.totalRedemptions}
            </p>
            <p className="text-sm text-text-muted">
              {currentLang === "ro" ? "Reduceri folosite" : "Discounts used"}
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-2xl font-bold text-text">
              {user.invitesRemaining}
            </p>
            <p className="text-sm text-text-muted">
              {currentLang === "ro" ? "Invitații disponibile" : "Invites left"}
            </p>
          </div>
        </div>

        {/* Account info */}
        <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
          <div className="px-4 py-3 bg-gray-50 border-b border-border">
            <p className="font-medium text-text">
              {currentLang === "ro" ? "Informații cont" : "Account info"}
            </p>
          </div>

          <div className="divide-y divide-border">
            <div className="flex items-center gap-3 p-4">
              <Envelope size={20} className="text-text-muted" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-text-muted">Email</p>
                <p className="text-text truncate">{user.email}</p>
              </div>
              {user.phoneVerified && (
                <CheckCircle size={20} className="text-success" />
              )}
            </div>

            <div className="flex items-center gap-3 p-4">
              <Phone size={20} className="text-text-muted" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-text-muted">
                  {currentLang === "ro" ? "Telefon" : "Phone"}
                </p>
                <p className="text-text">{user.phone}</p>
              </div>
              {user.phoneVerified && (
                <CheckCircle size={20} className="text-success" />
              )}
            </div>

            <div className="flex items-center gap-3 p-4">
              <Calendar size={20} className="text-text-muted" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-text-muted">
                  {currentLang === "ro" ? "Membru din" : "Member since"}
                </p>
                <p className="text-text">{formatDate(user.createdAt)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Gear */}
        <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
          <div className="px-4 py-3 bg-gray-50 border-b border-border">
            <p className="font-medium text-text">
              {currentLang === "ro" ? "Setări" : "Gear"}
            </p>
          </div>

          <div className="divide-y divide-border">
            <button
              onClick={() => changeLanguage(currentLang === "ro" ? "en" : "ro")}
              className="flex items-center gap-3 p-4 w-full text-left hover:bg-gray-50"
            >
              <Globe size={20} className="text-text-muted" />
              <div className="flex-1">
                <p className="text-text">
                  {currentLang === "ro" ? "Limbă" : "Language"}
                </p>
              </div>
              <span className="text-text-muted">
                {currentLang === "ro" ? "Română" : "English"}
              </span>
              <CaretRight size={20} className="text-text-muted" />
            </button>

            <button className="flex items-center gap-3 p-4 w-full text-left hover:bg-gray-50">
              <Bell size={20} className="text-text-muted" />
              <div className="flex-1">
                <p className="text-text">
                  {currentLang === "ro" ? "Notificări" : "Notifications"}
                </p>
              </div>
              <CaretRight size={20} className="text-text-muted" />
            </button>

            <button 
              onClick={() => setShowGdprInfo(true)}
              className="flex items-center gap-3 p-4 w-full text-left hover:bg-gray-50"
            >
              <Shield size={20} className="text-text-muted" />
              <div className="flex-1">
                <p className="text-text">
                  {currentLang === "ro" ? "Confidențialitate" : "Privacy"}
                </p>
              </div>
              <CaretRight size={20} className="text-text-muted" />
            </button>

            <button
              onClick={async () => {
                if (!userId || !exportData) return;
                setIsExporting(true);
                try {
                  const dataStr = JSON.stringify(exportData, null, 2);
                  const dataBlob = new Blob([dataStr], { type: "application/json" });
                  const url = URL.createObjectURL(dataBlob);
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = `suuper-data-${Date.now()}.json`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(url);
                } catch (err) {
                  console.error("Export error:", err);
                } finally {
                  setIsExporting(false);
                }
              }}
              disabled={!exportData || isExporting}
              className="flex items-center gap-3 p-4 w-full text-left hover:bg-gray-50"
            >
              <Download size={20} className="text-text-muted" />
              <div className="flex-1">
                <p className="text-text">
                  {currentLang === "ro" ? "Exportă datele mele" : "Export my data"}
                </p>
                <p className="text-xs text-text-muted">
                  {currentLang === "ro" ? "GDPR - Descarcă toate datele tale" : "GDPR - Download all your data"}
                </p>
              </div>
              {isExporting ? (
                <div className="w-5 h-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              ) : (
                <CaretRight size={20} className="text-text-muted" />
              )}
            </button>
          </div>
        </div>

        {/* Cancel subscription button */}
        {user.membershipStatus === "active" && !user.subscriptionCancelledAt && (
          <button
            onClick={() => setShowCancelConfirm(true)}
            className="w-full py-2 text-error text-sm font-medium hover:underline"
          >
            {currentLang === "ro" ? "Anulează abonamentul" : "Cancel subscription"}
          </button>
        )}

        {/* Delete Account button */}
        <button
          onClick={() => setShowDeleteConfirm(true)}
          className="w-full py-2 text-error text-sm font-medium hover:underline"
        >
          {currentLang === "ro" ? "Șterge contul" : "Delete account"}
        </button>

        {/* Logout */}
        <button
          onClick={handleLogout}
          className="flex items-center justify-center gap-2 w-full py-3 bg-error/10 text-error font-semibold rounded-xl"
        >
          <SignOut size={20} />
          {currentLang === "ro" ? "Deconectare" : "Log out"}
        </button>

        {/* Version */}
        <p className="text-center text-xs text-text-muted">
          Suuper v1.0.0
        </p>
      </div>
      {/* GDPR Info Modal */}
      {showGdprInfo && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-text">
                {currentLang === "ro" ? "Informații GDPR" : "GDPR Information"}
              </h3>
              <button
                onClick={() => setShowGdprInfo(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <XCircle size={20} className="text-text-muted" />
              </button>
            </div>
            <div className="space-y-4 text-sm text-text">
              <div>
                <h4 className="font-semibold mb-2">
                  {currentLang === "ro" ? "Ce date păstrăm?" : "What data do we keep?"}
                </h4>
                <ul className="list-disc list-inside space-y-1 text-text-muted">
                  <li>
                    {currentLang === "ro"
                      ? "Date personale: email, telefon, preferințe"
                      : "Personal data: email, phone, preferences"}
                  </li>
                  <li>
                    {currentLang === "ro"
                      ? "Istoric reduceri: pentru parteneri și facturare"
                      : "Redemption history: for partners and billing"}
                  </li>
                  <li>
                    {currentLang === "ro"
                      ? "Abonamente: pentru obligații legale cu Stripe"
                      : "Subscriptions: for legal obligations with Stripe"}
                  </li>
                  <li>
                    {currentLang === "ro"
                      ? "Alerte fraudă: pentru securitate"
                      : "Fraud alerts: for security"}
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-2">
                  {currentLang === "ro" ? "Perioada de retenție" : "Retention period"}
                </h4>
                <p className="text-text-muted">
                  {currentLang === "ro"
                      ? "Datele tale vor fi păstrate 60 zile după ștergerea contului pentru prevenirea fraudului și abuzurilor. După această perioadă, datele sensibile (email, telefon, device fingerprints) vor fi anonimizate automat."
                      : "Your data will be kept for 60 days after account deletion for fraud and abuse prevention. After this period, sensitive data (email, phone, device fingerprints) will be automatically anonymized."}
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">
                  {currentLang === "ro" ? "Anonimizare" : "Anonymization"}
                </h4>
                <p className="text-text-muted">
                  {currentLang === "ro"
                      ? "După 60 zile, email-ul și telefonul vor fi înlocuite cu valori anonime. Datele de facturare (reduceri, abonamente) vor rămâne pentru obligații legale, dar fără legătură cu identitatea ta."
                      : "After 60 days, email and phone will be replaced with anonymous values. Billing data (redemptions, subscriptions) will remain for legal obligations, but without connection to your identity."}
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">
                  {currentLang === "ro" ? "Ștergere completă" : "Complete deletion"}
                </h4>
                <p className="text-text-muted">
                  {currentLang === "ro"
                      ? "Dacă selectezi opțiunea de ștergere completă, după anonimizare (60 zile), contul tău va fi șters definitiv din baza de date. Datele de facturare vor rămâne pentru obligații legale, dar fără legătură cu identitatea ta."
                      : "If you select the complete deletion option, after anonymization (60 days), your account will be permanently deleted from the database. Billing data will remain for legal obligations, but without connection to your identity."}
                </p>
              </div>
              <div className="pt-4 border-t border-border">
                <button
                  onClick={() => setShowGdprInfo(false)}
                  className="w-full py-3 bg-primary text-white font-semibold rounded-xl"
                >
                  {currentLang === "ro" ? "Am înțeles" : "I understand"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
